import { useTranslation } from 'react-i18next'
import { useBulkStatusUpdate } from '../../hooks'
import type { MonitoringItem, SimplifiedStatus } from '../../types/collection-targets'
import { SIMPLIFIED_STATUS_LABELS, SIMPLIFIED_STATUS_COLOURS } from '../../types/collection-targets'

type SortColumn = 'hostname' | 'status' | 'collectionNumber' | 'completedAt' | 'request'

type SortDirection = 'asc' | 'desc'

const SIMPLIFIED_STATUS_TO_TARGET: Record<SimplifiedStatus, string> = {
  requested: 'pending',
  returned: 'returned',
  issue: 'failed',
}

const ORDINAL_KEYS: Record<number, string> = {
  1: 'ordinal1',
  2: 'ordinal2',
  3: 'ordinal3',
}

interface StatusBadgeProps {
  status: SimplifiedStatus
}

function StatusBadge({ status }: StatusBadgeProps): React.ReactElement {
  return (
    <span
      className="inline-flex rounded px-2 py-0.5 text-xs font-[var(--font-mono)] font-medium tracking-wider text-white uppercase"
      style={{ backgroundColor: SIMPLIFIED_STATUS_COLOURS[status] }}
    >
      {SIMPLIFIED_STATUS_LABELS[status]}
    </span>
  )
}

interface OrdinalDisplayProps {
  num: number
}

function OrdinalDisplay({ num }: OrdinalDisplayProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const key = ORDINAL_KEYS[num]
  if (key) {
    return <span>{t(`collections.monitoring.${key}` as never)}</span>
  }
  return <span>{t('collections.monitoring.ordinalN', { num })}</span>
}

interface TableHeaderCellProps {
  column: SortColumn
  label: string
  sortColumn: SortColumn
  sortDirection: SortDirection
  onSort: (col: SortColumn) => void
}

function TableHeaderCell({
  column,
  label,
  sortColumn,
  sortDirection,
  onSort,
}: TableHeaderCellProps): React.ReactElement {
  const isActive = sortColumn === column
  return (
    <th className="px-3 py-2 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
      <button
        onClick={() => {
          onSort(column)
        }}
        className="flex items-center gap-1 transition-colors hover:text-slate-900 dark:hover:text-slate-100"
      >
        {label}
        {isActive && (
          <span className="text-[var(--accent)]">{sortDirection === 'asc' ? '↑' : '↓'}</span>
        )}
      </button>
    </th>
  )
}

interface RowActionsProps {
  item: MonitoringItem
  caseId: string
  onUpdated: () => void
}

function RowActions({ item, caseId, onUpdated }: RowActionsProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { update, updating } = useBulkStatusUpdate()

  const handleStatusSelect = (value: string): void => {
    if (!value) return
    void (async () => {
      await update(caseId, item.collectionRequestId, {
        targetIds: [item.targetId],
        newStatus: value,
      })
      onUpdated()
    })()
  }

  return (
    <select
      disabled={updating}
      value=""
      onChange={(ev) => {
        handleStatusSelect(ev.target.value)
      }}
      className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
      aria-label={t('collections.monitoring.updateStatus')}
    >
      <option value="">{t('collections.monitoring.updateStatus')}</option>
      {(Object.keys(SIMPLIFIED_STATUS_TO_TARGET) as SimplifiedStatus[]).map((st) => (
        <option key={st} value={SIMPLIFIED_STATUS_TO_TARGET[st]}>
          {SIMPLIFIED_STATUS_LABELS[st]}
        </option>
      ))}
    </select>
  )
}

interface SortConfig {
  column: SortColumn
  direction: SortDirection
}

function sortItems(items: MonitoringItem[], config: SortConfig): MonitoringItem[] {
  return [...items].sort((aa: MonitoringItem, bb: MonitoringItem) => {
    let result = 0
    switch (config.column) {
      case 'hostname': {
        result = aa.hostname.localeCompare(bb.hostname)
        break
      }
      case 'status': {
        result = aa.simplifiedStatus.localeCompare(bb.simplifiedStatus)
        break
      }
      case 'collectionNumber': {
        result = aa.collectionNumber - bb.collectionNumber
        break
      }
      case 'completedAt': {
        result = (aa.completedAt ?? '').localeCompare(bb.completedAt ?? '')
        break
      }
      case 'request': {
        result = aa.requestName.localeCompare(bb.requestName)
        break
      }
    }
    return config.direction === 'asc' ? result : -result
  })
}

interface MonitoringTableHeaderProps {
  sortColumn: SortColumn
  sortDirection: SortDirection
  onSort: (col: SortColumn) => void
}

function MonitoringTableHeader({
  sortColumn,
  sortDirection,
  onSort,
}: MonitoringTableHeaderProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <thead className="bg-slate-50 dark:bg-slate-800/50">
      <tr>
        <TableHeaderCell
          column="hostname"
          label={t('collections.monitoring.hostname')}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <th className="px-3 py-2 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
          {t('collections.monitoring.deviceType')}
        </th>
        <th className="px-3 py-2 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
          {t('collections.monitoring.os')}
        </th>
        <th className="px-3 py-2 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
          {t('collections.monitoring.ipAddresses')}
        </th>
        <th className="px-3 py-2 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
          {t('collections.monitoring.evidenceId')}
        </th>
        <TableHeaderCell
          column="status"
          label={t('collections.monitoring.status')}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <TableHeaderCell
          column="collectionNumber"
          label={t('collections.monitoring.collectionNumber')}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <TableHeaderCell
          column="request"
          label={t('collections.monitoring.request')}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <TableHeaderCell
          column="completedAt"
          label={t('collections.monitoring.completedAt')}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <th className="px-3 py-2 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
          {t('collections.monitoring.actions')}
        </th>
      </tr>
    </thead>
  )
}

interface MonitoringTableRowProps {
  item: MonitoringItem
  caseId: string
  onNavigateToRequest: (requestId: string) => void
  onRefetch: () => void
}

function MonitoringTableRow({
  item,
  caseId,
  onNavigateToRequest,
  onRefetch,
}: MonitoringTableRowProps): React.ReactElement {
  return (
    <tr className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/30">
      <td className="px-3 py-2 text-xs font-[var(--font-mono)] font-medium text-slate-900 dark:text-slate-100">
        {item.hostname}
      </td>
      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
        {item.deviceType ?? '-'}
      </td>
      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
        {item.operatingSystem ?? '-'}
      </td>
      <td className="px-3 py-2 text-xs font-[var(--font-mono)] text-slate-600 dark:text-slate-400">
        {item.ipAddresses.length > 0 ? item.ipAddresses.join(', ') : '-'}
      </td>
      <td className="px-3 py-2 text-xs font-[var(--font-mono)] text-slate-600 dark:text-slate-400">
        {item.evidenceId ?? '-'}
      </td>
      <td className="px-3 py-2">
        <StatusBadge status={item.simplifiedStatus} />
      </td>
      <td className="px-3 py-2 text-center text-xs text-slate-600 dark:text-slate-400">
        <OrdinalDisplay num={item.collectionNumber} />
      </td>
      <td className="px-3 py-2">
        <button
          onClick={() => {
            onNavigateToRequest(item.collectionRequestId)
          }}
          className="text-xs font-[var(--font-mono)] text-[var(--accent)] hover:underline"
        >
          {item.requestName}
          {item.requestCode && (
            <span className="ml-1 text-slate-400 dark:text-slate-500">({item.requestCode})</span>
          )}
        </button>
      </td>
      <td className="px-3 py-2 text-xs font-[var(--font-mono)] text-slate-500 dark:text-slate-400">
        {item.completedAt ?? '-'}
      </td>
      <td className="px-3 py-2">
        <RowActions item={item} caseId={caseId} onUpdated={onRefetch} />
      </td>
    </tr>
  )
}

export interface MonitoringTableProps {
  items: MonitoringItem[]
  caseId: string
  sortColumn: SortColumn
  sortDirection: SortDirection
  onSort: (col: SortColumn) => void
  onNavigateToRequest: (requestId: string) => void
  onRefetch: () => void
}

export function MonitoringTable({
  items,
  caseId,
  sortColumn,
  sortDirection,
  onSort,
  onNavigateToRequest,
  onRefetch,
}: MonitoringTableProps): React.ReactElement {
  const sorted = sortItems(items, { column: sortColumn, direction: sortDirection })

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
      <table className="w-full text-sm">
        <MonitoringTableHeader
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {sorted.map((item) => (
            <MonitoringTableRow
              key={item.targetId}
              item={item}
              caseId={caseId}
              onNavigateToRequest={onNavigateToRequest}
              onRefetch={onRefetch}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}
